import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, X, Send, ArrowRight, Home, Search, Bell } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "novo", label: "Novo" },
  { key: "em_atendimento", label: "Em atendimento" },
  { key: "visita_agendada", label: "Visita agendada" },
  { key: "negociacao", label: "Negociação" },
  { key: "ganho", label: "Ganho" },
  { key: "perdido", label: "Perdido" },
];

const nextStage = (s) => {
  const i = STAGES.findIndex((x) => x.key === s);
  return i >= 0 && i < STAGES.length - 2 ? STAGES[i + 1].key : null;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : "");

const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export default function AdminBrokerApp() {
  const { user, headers, logout } = useAdminAuth();
  const [leads, setLeads] = useState(null);
  const [properties, setProperties] = useState([]);
  const [stage, setStage] = useState("all");
  const [onlyMine, setOnlyMine] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, p] = await Promise.all([
        axios.get(`${API}/crm/leads`, { headers: headers() }),
        axios.get(`${API}/properties`),
      ]);
      setLeads(l.data);
      setProperties(p.data);
    } catch {
      setLeads([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useEffect(() => {
    if (!user) return;
    const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
    setPushSupported(supported);
    if (supported) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushEnabled(!!sub))
        .catch(() => {});
    }
  }, [user]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permita as notificações nas configurações do navegador.");
        return;
      }
      const { data } = await axios.get(`${API}/push/vapid-public`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, { headers: headers() });
      setPushEnabled(true);
      toast.success("Notificações ativadas neste aparelho! Novos contatos chegam aqui.");
    } catch {
      toast.error("Não foi possível ativar as notificações neste aparelho.");
    } finally {
      setPushBusy(false);
    }
  };

  const propTitle = (id) => properties.find((p) => p.id === id)?.title || null;
  const stageLabel = (k) => STAGES.find((s) => s.key === k)?.label || "Novo";

  const filtered = (leads || []).filter((l) => {
    if (onlyMine && l.assigned_to && l.assigned_to !== user?.email) return false;
    if (onlyMine && !l.assigned_to && user?.role !== "admin") return false;
    if (stage !== "all" && (l.status || "novo") !== stage) return false;
    if (search && !`${l.nome} ${l.telefone}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const advance = async (lead) => {
    const ns = nextStage(lead.status || "novo");
    if (!ns) return;
    try {
      await axios.put(`${API}/crm/leads/${lead.id}`, { status: ns }, { headers: headers() });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: ns } : l)));
      setSelected((s) => (s ? { ...s, status: ns } : s));
      toast.success(`Movido para "${stageLabel(ns)}"`);
    } catch {
      toast.error("Não foi possível mover.");
    }
  };

  const claim = async (lead) => {
    try {
      await axios.put(`${API}/crm/leads/${lead.id}`, { assigned_to: user.email }, { headers: headers() });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, assigned_to: user.email } : l)));
      setSelected((s) => (s ? { ...s, assigned_to: user.email } : s));
      toast.success("Contato atribuído a você.");
    } catch {
      toast.error("Não foi possível atribuir.");
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${selected.id}/notes`, { text: note.trim() }, { headers: headers() });
      const newNote = r.data;
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? { ...l, notes: [...(l.notes || []), newNote] } : l)));
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), newNote] }));
      setNote("");
    } catch {
      toast.error("Não foi possível salvar.");
    } finally {
      setSavingNote(false);
    }
  };

  const next = selected ? nextStage(selected.status || "novo") : null;
  const nextLabel = next ? stageLabel(next) : null;
  const phoneDigits = (selected?.telefone || "").replace(/\D/g, "");

  return (
    <main className="mx-auto min-h-screen max-w-2xl px-4 pt-20 pb-32" data-testid="broker-app">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-6 mb-5">
        <h1 className="font-heading text-3xl font-black tracking-tighter text-seed-deep" data-testid="app-title">
          Olá, {user?.name?.split(" ")[0] || "corretor(a)"}
        </h1>
        <p className="mt-1 text-sm text-seed-deep/50">Sua carteira de contatos, direto da rua.</p>
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-full bg-white p-1.5 shadow-sm" data-testid="app-search">
        <Search size={16} className="ml-3 shrink-0 text-seed-deep/40" />
        <input
          placeholder="Buscar por nome ou telefone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-full bg-transparent text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
          data-testid="app-search-input"
        />
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="app-stage-chips">
        <button
          onClick={() => setStage("all")}
          data-testid="app-stage-all"
          className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${stage === "all" ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep/60"}`}
        >
          Todos ({(leads || []).length})
        </button>
        {STAGES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStage(s.key)}
            data-testid={`app-stage-${s.key}`}
            className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${stage === s.key ? "bg-seed-salad text-white" : "bg-white text-seed-deep/60"}`}
          >
            {s.label} ({(leads || []).filter((l) => (l.status || "novo") === s.key).length})
          </button>
        ))}
      </div>

      <button
        onClick={() => setOnlyMine((v) => !v)}
        data-testid="app-toggle-mine"
        className={`mb-5 w-full rounded-full py-3 font-heading text-sm font-bold transition-colors ${onlyMine ? "bg-seed-vivid text-seed-deep" : "bg-white text-seed-deep/60"}`}
      >
        {onlyMine ? "Mostrando: minha carteira" : "Mostrando: todos os contatos"}
      </button>

      {pushSupported && !pushEnabled && (
        <button
          onClick={enablePush}
          disabled={pushBusy}
          data-testid="app-enable-push"
          className="mb-5 flex w-full items-center justify-center gap-2 rounded-full border-2 border-seed-vivid bg-seed-vivid/10 py-3 font-heading text-sm font-bold text-seed-deep transition-transform active:scale-[0.98] disabled:opacity-60"
        >
          <Bell size={16} /> {pushBusy ? "Ativando..." : "Ativar avisos de novos contatos"}
        </button>
      )}
      {pushSupported && pushEnabled && (
        <p className="mb-5 flex items-center justify-center gap-2 rounded-full bg-seed-salad/10 py-2.5 text-xs font-bold text-seed-salad" data-testid="app-push-on">
          <Bell size={14} /> Notificações ativas neste aparelho
        </p>
      )}

      {leads === null ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm" data-testid="app-empty">Nenhum contato aqui.</p>
      ) : (
        <div className="space-y-3" data-testid="app-lead-list">
          {filtered.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelected(l)}
              data-testid={`app-lead-${l.id.slice(0, 8)}`}
              className="w-full rounded-xl bg-white p-5 text-left shadow-sm transition-shadow active:shadow-md"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="font-heading text-base font-bold text-seed-deep">{l.nome}</p>
                <span className="shrink-0 rounded-full bg-seed-salad/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-seed-salad">
                  {stageLabel(l.status || "novo")}
                </span>
              </div>
              <p className="mt-1 text-sm text-seed-deep/60">{l.telefone}</p>
              {propTitle(l.property_id) && (
                <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-seed-deep/50">
                  <Home size={12} className="text-seed-salad" /> {propTitle(l.property_id)}
                </p>
              )}
              <div className="mt-2 flex items-center justify-between text-[11px] font-semibold text-seed-deep/40">
                <span>{fmtDate(l.created_at)}</span>
                {!l.assigned_to && <span className="text-seed-salad">Sem responsável — toque para assumir</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-[60] bg-seed-deep/40 backdrop-blur-sm"
              data-testid="app-sheet-overlay"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl"
              data-testid="app-sheet"
            >
              <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-seed-deep/15" />
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tight text-seed-deep" data-testid="app-sheet-name">{selected.nome}</h2>
                  <p className="mt-0.5 text-xs font-bold uppercase tracking-widest text-seed-salad">{stageLabel(selected.status || "novo")}</p>
                </div>
                <button onClick={() => setSelected(null)} data-testid="app-sheet-close" className="text-seed-deep/40">
                  <X size={24} />
                </button>
              </div>

              {selected.mensagem && (
                <p className="mt-4 rounded-lg bg-seed-off p-4 text-sm leading-relaxed text-seed-deep/70">{selected.mensagem}</p>
              )}
              {propTitle(selected.property_id) && (
                <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-seed-deep/70">
                  <Home size={15} className="text-seed-salad" /> {propTitle(selected.property_id)}
                </p>
              )}

              <div className="mt-5 grid grid-cols-2 gap-3">
                <a
                  href={`tel:+55${phoneDigits}`}
                  data-testid="app-call"
                  className="flex items-center justify-center gap-2 rounded-xl bg-seed-deep py-4 font-heading text-sm font-bold text-seed-off"
                >
                  <Phone size={17} /> Ligar
                </a>
                <a
                  href={`https://wa.me/55${phoneDigits}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="app-whatsapp"
                  className="flex items-center justify-center gap-2 rounded-xl bg-seed-vivid py-4 font-heading text-sm font-bold text-seed-deep"
                >
                  <MessageCircle size={17} /> WhatsApp
                </a>
              </div>

              {!selected.assigned_to && (
                <button onClick={() => claim(selected)} data-testid="app-claim" className="mt-3 w-full rounded-xl border-2 border-seed-salad py-4 font-heading text-sm font-bold text-seed-salad">
                  Assumir este contato
                </button>
              )}
              {next && (
                <button onClick={() => advance(selected)} data-testid="app-advance" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-seed-salad py-4 font-heading text-sm font-bold text-white">
                  Avançar para "{nextLabel}" <ArrowRight size={16} />
                </button>
              )}

              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-seed-deep/50">Anotações</p>
                <div className="mt-2 space-y-2" data-testid="app-notes">
                  {(selected.notes || []).slice(-3).map((n) => (
                    <div key={n.id} className="rounded-lg bg-seed-off p-3 text-sm text-seed-deep/80">{n.text}</div>
                  ))}
                  {(selected.notes || []).length === 0 && <p className="text-sm text-seed-deep/40">Nenhuma anotação.</p>}
                </div>
                <form onSubmit={addNote} className="mt-3 flex gap-2">
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Anotação rápida..."
                    className="h-12 flex-1 rounded-lg bg-seed-off px-4 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40"
                    data-testid="app-note-input"
                  />
                  <button type="submit" disabled={savingNote} data-testid="app-note-submit" className="flex h-12 w-12 items-center justify-center rounded-lg bg-seed-deep text-seed-off disabled:opacity-60" aria-label="Salvar anotação">
                    <Send size={16} />
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}
